
// apps/admin-ui/src/api/auth.ts
// Authentication API for Admin UI
// ALL RIGHTS RESERVED

import type { Employee } from '../utils/auth';
import { API_CONFIG } from '../config/api';

const IDP_URL = API_CONFIG.idp;

// =============================================================================
// TYPES
// =============================================================================

export interface EmployeeLoginResponse {
  message: string;
}

export interface EmployeeVerifyResponse {
  message: string;
  employee: Employee;
  certificate: {
    fingerprint: string;
    expiresAt: string;
  };
  devMode?: boolean;
}

export interface EmployeeListResponse {
  employees: Employee[];
}

// =============================================================================
// API ERROR
// =============================================================================

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// =============================================================================
// AUTH API
// =============================================================================

export async function employeeLogin(email: string): Promise<EmployeeLoginResponse> {
  const response = await fetch(`${IDP_URL}/employee/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Login request failed' }));
    throw new ApiError(response.status, error.code || 'LOGIN_FAILED', error.message);
  }

  return response.json();
}

export async function employeeVerify(email: string, otp: string): Promise<EmployeeVerifyResponse> {
  const response = await fetch(`${IDP_URL}/employee/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Verification failed' }));
    throw new ApiError(response.status, error.code || 'VERIFY_FAILED', error.message);
  }

  return response.json();
}

export async function getEmployees(): Promise<Employee[]> {
  const response = await fetch(`${IDP_URL}/employee/list`, {
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch employees' }));
    throw new ApiError(response.status, error.code || 'FETCH_FAILED', error.message);
  }

  const data: EmployeeListResponse = await response.json();
  return data.employees;
}

